import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FiSearch, FiLogOut, FiSettings, FiUser, FiChevronDown, FiX } from 'react-icons/fi';

function Topbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);
  
  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearch(false);
      }
    }

    if (isOpen || showSearch) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, showSearch]);

  // Keyboard shortcut for search (Cmd/Ctrl + K)
  useEffect(() => {
    function handleKeyDown(event) {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        setShowSearch(true);
      }
      if (event.key === 'Escape') {
        setShowSearch(false);
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Don't render if user is not loaded
  if (!user) {
    return null;
  }
  
  const firstName = user?.name?.split(' ')[0] || 'User';
  const initials = user?.name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || 'U';

  // Get portal name based on user role
  const ownerTenantName = localStorage.getItem('ownerTenantName');
  const ownerTenantId = localStorage.getItem('ownerTenantId');

  const getPortalName = () => {
    if (user?.role === 'restaurant_admin') return 'Restaurant Portal';
    if (user?.role === 'staff') return 'Staff Portal';
    if (user?.is_owner || user?.role === 'owner') {
      return ownerTenantName ? `Owner View: ${ownerTenantName}` : 'Owner Portal';
    }
    return 'ElyDine';
  };

  const clearOwnerTenant = () => {
    localStorage.removeItem('ownerTenantId');
    localStorage.removeItem('ownerTenantName');
    navigate('/owner/dashboard');
  };

  const handleSignOut = () => {
    logout();
    // Redirect to appropriate login page based on role
    if (user?.role === 'restaurant_admin') {
      navigate('/login/restaurant');
    } else if (user?.role === 'staff') {
      navigate('/login/staff');
    } else {
      navigate('/');
    }
  };

  const getSettingsPath = () => {
    if (user?.role === 'restaurant_admin') {
      return '/restaurant/settings';
    }
    // Add other role settings paths as needed
    return '/restaurant/settings';
  };

  return (
    <header className="sticky top-0 z-40 flex h-16 items-center justify-between bg-gray-50 px-6">
      {/* Left Side - Search Bar */}
      <div className="flex flex-1 items-center max-w-2xl" ref={searchRef}>
        <div className="relative w-full">
          <FiSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Search orders, guests, menu items..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setShowSearch(true)}
            className="pl-10 pr-20 w-full bg-white border border-gray-200 rounded-lg text-sm h-9 focus:bg-white focus:border-gray-300 focus:ring-1 focus:ring-gray-300 focus:outline-none transition-all shadow-sm"
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
            <kbd className="hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border border-gray-200 bg-white px-1.5 font-mono text-[10px] font-medium text-gray-500">
              <span className="text-[8px]">⌘</span>K
            </kbd>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiX className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Right Side - Actions & User */}
      <div className="flex items-center gap-2 ml-6">
        {(user?.is_owner || user?.role === 'owner') && ownerTenantId && (
          <button
            onClick={clearOwnerTenant}
            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-900 text-white hover:bg-gray-800 transition-colors"
          >
            Exit Owner View
          </button>
        )}
        {/* User Profile */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-gray-900 to-gray-700 flex items-center justify-center text-white font-medium text-xs flex-shrink-0 ring-2 ring-gray-50">
              {initials}
            </div>
            <div className="hidden md:flex flex-col items-start min-w-0">
              <span className="text-sm font-medium text-gray-900 truncate max-w-[100px]">{firstName}</span>
              <span className="text-xs text-gray-500 truncate max-w-[100px]">{getPortalName()}</span>
            </div>
            <FiChevronDown className={`h-4 w-4 text-gray-500 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Dropdown Menu */}
          {isOpen && (
            <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
              {/* User Info */}
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-gray-900 to-gray-700 flex items-center justify-center text-white font-medium text-sm">
                    {initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{user?.name || 'User'}</p>
                    <p className="text-xs text-gray-500 truncate">{getPortalName()}</p>
                  </div>
                </div>
              </div>
              
              {/* Menu Items */}
              <div className="py-1">
                <button
                  onClick={() => {
                    setIsOpen(false);
                    navigate(getSettingsPath());
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                >
                  <FiUser className="h-4 w-4 text-gray-400" />
                  <span>Profile</span>
                </button>
                <button
                  onClick={() => {
                    setIsOpen(false);
                    navigate(getSettingsPath());
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                >
                  <FiSettings className="h-4 w-4 text-gray-400" />
                  <span>Settings</span>
                </button>
              </div>

              {/* Sign Out */}
              <div className="border-t border-gray-100 pt-1">
                <button
                  onClick={handleSignOut}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors text-left"
                >
                  <FiLogOut className="h-4 w-4" />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export default Topbar;
